import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';

export const useSubscription = () => {
  const queryClient = useQueryClient();

  // Fetch subscription plans
  const {
    data: plans,
    isLoading: isLoadingPlans,
    error: plansError
  } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const response = await container.subscriptionRepository.getPlans();
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    }
  });

  // Fetch current subscription
  const {
    data: currentSubscription,
    isLoading: isLoadingSubscription,
    error: subscriptionError
  } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = await container.subscriptionRepository.getCurrentSubscription();
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    }
  });

  // Fetch usage limits
  const {
    data: usageLimits,
    isLoading: isLoadingUsageLimits,
    error: usageLimitsError
  } = useQuery({
    queryKey: ['usage-limits'],
    queryFn: async () => {
      const response = await container.subscriptionRepository.getUsageLimits();
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    }
  });

  // Create subscription mutation
  const createSubscriptionMutation = useMutation({
    mutationFn: async ({ planId, paymentMethodId }: { planId: string; paymentMethodId?: string }) => {
      const response = await container.subscriptionRepository.createSubscription({
        planId,
        paymentMethodId,
      });
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    }
  });

  // Create trial subscription mutation
  const createTrialSubscriptionMutation = useMutation({
    mutationFn: async () => {
      const response = await container.subscriptionRepository.createTrialSubscription();
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    }
  });

  // Update subscription mutation
  const updateSubscriptionMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: { planId?: string; cancelAtPeriodEnd?: boolean } }) => {
      const response = await container.subscriptionRepository.updateSubscription(id, updates);
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    }
  });

  // Report usage mutation
  const reportUsageMutation = useMutation({
    mutationFn: async ({ companyId, orderCount }: { companyId: string; orderCount: number }) => {
      const response = await container.subscriptionRepository.reportUsage({
        companyId,
        orderCount,
      });
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usage-limits'] });
    }
  });

  // Create portal session mutation
  const createPortalSessionMutation = useMutation({
    mutationFn: async () => {
      const response = await container.subscriptionRepository.createPortalSession();
      if (response.status === 'error') throw new Error(response.error.message);
      return response.data;
    }
  });

  // Computed getters
  const canCreateProduct = () => {
    return usageLimits ? !usageLimits.productLimitReached : true;
  };

  const canCreatePurchaseOrder = () => {
    return usageLimits ? usageLimits.canCreatePurchaseOrders : false;
  };

  const isTrialActive = () => {
    return currentSubscription?.status === 'trialing' && 
           currentSubscription?.trialEndDate && 
           new Date(currentSubscription.trialEndDate) > new Date();
  };

  const getTrialDaysRemaining = () => {
    if (!currentSubscription?.trialEndDate || currentSubscription.status !== 'trialing') return null;
    
    const trialEnd = new Date(currentSubscription.trialEndDate);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  const getProductUsagePercentage = () => {
    if (!usageLimits || usageLimits.maxProducts === -1) return 0;
    return (usageLimits.currentProducts / usageLimits.maxProducts) * 100;
  };

  const getOrderUsagePercentage = () => {
    if (!usageLimits || usageLimits.maxOrdersPerMonth === -1) return 0;
    return (usageLimits.currentOrdersThisMonth / usageLimits.maxOrdersPerMonth) * 100;
  };

  return {
    // Data
    currentSubscription,
    plans: plans || [],
    usageLimits,
    
    // Loading states
    isLoading: isLoadingPlans || isLoadingSubscription || isLoadingUsageLimits,
    isLoadingPlans,
    isLoadingSubscription,
    isLoadingUsageLimits,
    
    // Error states
    plansError,
    subscriptionError,
    usageLimitsError,

    // Mutations
    createSubscription: createSubscriptionMutation.mutateAsync,
    createTrialSubscription: createTrialSubscriptionMutation.mutateAsync,
    updateSubscription: updateSubscriptionMutation.mutateAsync,
    reportUsage: reportUsageMutation.mutateAsync,
    createPortalSession: createPortalSessionMutation.mutateAsync,
    
    // Mutation states
    isCreating: createSubscriptionMutation.isLoading,
    isCreatingTrial: createTrialSubscriptionMutation.isLoading,
    isUpdating: updateSubscriptionMutation.isLoading,
    isReportingUsage: reportUsageMutation.isLoading,
    isCreatingPortalSession: createPortalSessionMutation.isLoading,

    // Computed getters
    canCreateProduct,
    canCreatePurchaseOrder,
    isTrialActive,
    getTrialDaysRemaining,
    getProductUsagePercentage,
    getOrderUsagePercentage,
  };
};
